import { Box, Flex, Tab, TabList, Tabs, Text, useColorModeValue } from '@chakra-ui/react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { HeatmapData, PLAYER_COLORS } from '../types';
import { translate } from '../../../i18n';

interface Props {
  heatmaps: Record<string, HeatmapData>;
}

function interpolateColor(value: number): string {
  // Blue (cold) → Yellow → Red (hot)
  if (value < 0.5) {
    const t = value * 2;
    const r = Math.round(t * 255);
    const g = Math.round(t * 255);
    const b = Math.round((1 - t) * 255);
    return `rgb(${r},${g},${b})`;
  }
  const t = (value - 0.5) * 2;
  const r = 255;
  const g = Math.round((1 - t) * 255);
  return `rgb(${r},${g},0)`;
}

function drawCourt(ctx: CanvasRenderingContext2D, w: number, h: number) {
  ctx.strokeStyle = 'rgba(255,255,255,0.6)';
  ctx.lineWidth = 1.5;

  // Court outline
  ctx.strokeRect(2, 2, w - 4, h - 4);

  // Net (center horizontal line)
  ctx.beginPath();
  ctx.moveTo(0, h / 2);
  ctx.lineTo(w, h / 2);
  ctx.stroke();

  // Service boxes
  const boxW = w * 0.3;
  const boxH = h * 0.25;

  // Top side service boxes
  ctx.strokeRect((w - boxW * 2) / 2, 2, boxW * 2, boxH);
  ctx.beginPath();
  ctx.moveTo(w / 2, 2);
  ctx.lineTo(w / 2, boxH + 2);
  ctx.stroke();

  // Bottom side service boxes
  ctx.strokeRect((w - boxW * 2) / 2, h - boxH - 2, boxW * 2, boxH);
  ctx.beginPath();
  ctx.moveTo(w / 2, h - boxH - 2);
  ctx.lineTo(w / 2, h - 2);
  ctx.stroke();
}

export default function CourtHeatmap({ heatmaps }: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const playerIds = useMemo(() => Object.keys(heatmaps), [heatmaps]);
  const [selectedIdx, setSelectedIdx] = useState(0);
  const bgCard = useColorModeValue('white', 'navy.800');
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const canvasBg = useColorModeValue('#EDF2F7', '#1a1a2e');

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || playerIds.length === 0) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const pid = playerIds[selectedIdx] || playerIds[0];
    const heatmap = heatmaps[pid];
    if (!heatmap) return;

    const { grid, grid_size } = heatmap;
    const [cols, rows] = grid_size;

    // HiDPI support
    const dpr = window.devicePixelRatio || 1;
    canvas.width = 400 * dpr;
    canvas.height = 300 * dpr;
    ctx.scale(dpr, dpr);

    const cw = 400;
    const ch = 300;
    const cellW = cw / cols;
    const cellH = ch / rows;

    // Background
    ctx.fillStyle = canvasBg;
    ctx.fillRect(0, 0, cw, ch);

    // Heatmap cells
    for (let r = 0; r < rows && r < grid.length; r++) {
      for (let c = 0; c < cols && c < grid[r].length; c++) {
        const val = grid[r][c];
        if (val > 0.01) {
          ctx.fillStyle = interpolateColor(val);
          ctx.globalAlpha = 0.3 + val * 0.7;
          ctx.fillRect(c * cellW, r * cellH, cellW, cellH);
        }
      }
    }

    ctx.globalAlpha = 1;
    drawCourt(ctx, cw, ch);
  }, [heatmaps, playerIds, selectedIdx, canvasBg]);

  useEffect(() => {
    draw();
  }, [draw]);

  if (playerIds.length === 0) {
    return (
      <Flex h="200px" align="center" justify="center">
        <Text color="secondaryGray.600">{translate('court_recording.no_heatmap_data')}</Text>
      </Flex>
    );
  }

  return (
    <Box bg={bgCard} borderRadius="12px" p={4}>
      <Flex align="center" gap={2} mb={3}>
        <Text fontSize="md" fontWeight="600" color={textColor}>{translate('court_recording.court_heatmap')}</Text>
        {playerIds.length > 0 && heatmaps[playerIds[0]]?.coordinate_system === 'court' && (
          <Text fontSize="xs" color="green.400" fontWeight="500">({translate('court_recording.court_coords')})</Text>
        )}
      </Flex>
      <Tabs
        index={selectedIdx}
        onChange={setSelectedIdx}
        variant="soft-rounded"
        colorScheme="green"
        size="sm"
        mb={3}
      >
        <TabList>
          {playerIds.map((pid, i) => (
            <Tab key={pid} color={PLAYER_COLORS[i % PLAYER_COLORS.length]}>
              {translate('court_recording.player')} {pid}
            </Tab>
          ))}
        </TabList>
      </Tabs>
      <canvas
        ref={canvasRef}
        width={400}
        height={300}
        style={{ width: '100%', maxWidth: '400px', borderRadius: '8px', margin: '0 auto', display: 'block' }}
      />
    </Box>
  );
}
